<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import {
  IUserResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {computed, onMounted, ref, watch} from 'vue'
import {usePermissionApiStore} from '@toolify/client/src/stores/api/PermissionApiStore/usePermissionApiStore'

import EntityItemContainer from '@toolify/client/src/modules/entities/EntityItem/EntityItemContainer.vue'
import {PermissionEntityType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionEntityType'
import {DynamicFilterPayloadValue} from '@toolify/server/src/services/FilterService/enum/DynamicFilterPayloadValue'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import ChooseComponent from '@toolify/client/src/modules/list/components/ChooseComponent.vue'
import ChooseItemComponent from '@toolify/client/src/modules/list/components/ChooseItemComponent.vue'
import ChooseItemSkeletonComponent from '@toolify/client/src/modules/list/components/ChooseItemSkeletonComponent.vue'
import {ChooseItemSkeletonShapeType} from '@toolify/client/src/modules/list/enum/ChooseItemSkeletonShapeType'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {Breakpoint, useResponsive} from '@toolify/client/src/composables/useResponsive'
import {IGroupResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IGroupResponseInstance'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {useI18n} from 'vue-i18n'
import {getSelectedItemsFirst} from '@toolify/client/src/modules/list/functions/getSelectedItemsFirst'

interface Emits {
  (e: 'choose', userId: string): void
  (e: 'chooseGroup', groupId: string): void
  (e: 'chooseDynamic', value: DynamicFilterPayloadValue.Me): void
  (e: 'exit'): void
  (e: 'tab'): void
  (e: 'shiftTab'): void
}
interface Props {
  resourceType: ResourceType
  resourceId: string
  selectedEntityIds: string[]
  selectedUserGroupIds?: string[]
  availableUserGroups?: IGroupResponseInstance[]
  showDynamicValues?: boolean,
  showUserGroups?: boolean,
  filteredEntityIds?: string[]
  availableValues?: IUserResponseInstance[]
  isFetching?:boolean
  hasMultipleValues?: boolean
  fillAllPlace?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  selectedUserGroupIds: null,
  availableUserGroups: null,
  showDynamicValues: false,
  showUserGroups: false,
  propertyId: null,
  availableValues: null,
  isFetching: false,
  filteredEntityIds: ()=>[],
  hasMultipleValues: true,
  fillAllPlace: false,
})
const emit = defineEmits<Emits>()

let users = _ref<IUserResponseInstance[]>(null)
let userGroups = _ref<IGroupResponseInstance[]>(null)
const search = _ref('')

// Whoever is chosen when the picker opens leads the list. The order holds while the picker is open, so a click
// does not move the row from under the pointer; until the first click it follows a selection that arrives late.
let hasChosen = false
const leadingUserIds = ref(new Set(props.selectedEntityIds ?? []))
const leadingUserGroupIds = ref(new Set(props.selectedUserGroupIds ?? []))
watch(() => [props.selectedEntityIds, props.selectedUserGroupIds], () => {
  if(hasChosen) {
    return
  }
  leadingUserIds.value = new Set(props.selectedEntityIds ?? [])
  leadingUserGroupIds.value = new Set(props.selectedUserGroupIds ?? [])
}, {deep: true})

const permissionApiStore = usePermissionApiStore()
const {isSmallerThan} = useResponsive()
const {t} = useI18n()

onMounted(async () => {
  if(!props.availableValues && !props.availableUserGroups && !props.isFetching) {
    const entityTypes = [PermissionEntityType.User]
    if (props.showUserGroups) entityTypes.push(PermissionEntityType.Group)
    
    const res = await permissionApiStore.getEntitiesByResource(props.resourceType, props.resourceId, entityTypes)
    
    users.value = res.entities
      .filter(e => e.entityType === PermissionEntityType.User)
      .map(e => e.payload as IUserResponseInstance)
      
    userGroups.value = res.entities
      .filter(e => e.entityType === PermissionEntityType.Group)
      .map(e => e.payload as IGroupResponseInstance)
  } else {
    users.value = props.availableValues
    userGroups.value = props.availableUserGroups
  }
})

watch(() => props.availableValues, async (value) => {
  if(value) {
    users.value = value
  }
})

watch(() => props.availableUserGroups, (value) => {
  if(value) {
    userGroups.value = value
  }
}, {immediate: true})

interface IEntityItem {
  title: string
  entityType: PermissionEntityType
  entityId: string
  thumbnailPayload: {entity?: IResponseEntity, imageUrl?: string, color?: unknown, iconNameType?: IconNameType}
  key: string
}

const entityItems = _computed<IEntityItem[]>(() => {
  if (!users.value && !userGroups.value) {
    return []
  }

  const me =  props.showDynamicValues ? [{
    id: DynamicFilterPayloadValue.Me,
    title: t('database.filters.me'),
    entityType: PermissionEntityType.User,
    entityId: DynamicFilterPayloadValue.Me,
    thumbnailPayload: {},
    key: DynamicFilterPayloadValue.Me,
  }] : []

  const userItems = (users.value || []).map(user => {
    const entity: IResponseEntity = {
      entityType: EntityType.User,
      entityId: user.id,
      payload: user,
    }
    return {
      id: user.id,
      title: user.nick,
      entityType: PermissionEntityType.User,
      entityId: user.id,
      key: `user-${user.id}`,
      thumbnailPayload: {
        entity,
      },
    }
  })

  const userGroupItems = (userGroups.value || []).map(group => {
    return {
      id: group.id,
      title: group.title,
      entityType: PermissionEntityType.Group,
      entityId: group.id,
      key: `group-${group.id}`,
      thumbnailPayload: {
        color: group.color,
        iconNameType: IconNameType.UserFriends,
      },
    }
  })

  const sortedUserGroupItems = getSelectedItemsFirst(userGroupItems, item => leadingUserGroupIds.value.has(item.entityId))
  const sortedUserItems = getSelectedItemsFirst(userItems, item => leadingUserIds.value.has(item.entityId))
  return [...sortedUserGroupItems, ...me, ...sortedUserItems].filter(item => item.title.toLowerCase().includes(search.value.toLowerCase()))
    .filter(item => {
      if (!props.filteredEntityIds) {
        return true
      }
      return !props.filteredEntityIds.includes(item.entityId)
    })
})

const separatorIndexes = computed<number[]>(() => {
  if(!userGroups.value?.length) {
    return []
  }
  return [userGroups.value.length - 1]
})

const onEntityClick = (index) => {
  const entityItem = entityItems.value[index]
  if(!entityItem) return
  hasChosen = true
  
  const entityId = entityItem.entityId
  if(entityId === DynamicFilterPayloadValue.Me) {
    emit('chooseDynamic', DynamicFilterPayloadValue.Me)
    return
  }
  
  if(entityItem.entityType === PermissionEntityType.Group) {
    emit('chooseGroup', entityId)
    return
  }
  
  emit('choose', entityId)
}

const onDynamicValueClick = (value) => {
  hasChosen = true
  emit('chooseDynamic', value)
}

const hasLoaded = _computed(() => !!users.value)

const selectedIndexes = _computed(() => {
  if(!entityItems.value) {
    return []
  }
  return entityItems.value
    .filter(entityItem => {
      if(entityItem.entityType === PermissionEntityType.Group) {
        return props.selectedUserGroupIds?.includes(entityItem.entityId) || false
      }
      return props.selectedEntityIds.includes(entityItem.entityId)
    })
    .map(entityItem => entityItems.value.indexOf(entityItem))
})

defineExpose({
  getUsers: () => {
    return entityItems.value.map(entityItem => {
      const entity = entityItem.thumbnailPayload.entity
      if(entity) {
        return {
          ...entity,
        }
      }
      return {
        id: entityItem.entityId,
        nick: entityItem.title,
        avatarUrl: entityItem.thumbnailPayload.imageUrl,
        userId: entityItem.entityId,
      }
    })
  },
})

const isFillAllPlace = computed(() => props.fillAllPlace || isSmallerThan(Breakpoint.Large).value)
</script>

<template>
  <ChooseComponent
    v-model:search="search"
    :selectedIndexes="selectedIndexes"
    :items="entityItems"
    :isLoading="!hasLoaded"
    :separatorIndexes="separatorIndexes"
    @close="emit('exit')"
    @chooseItem="onEntityClick"
    @tab="emit('tab')"
    @shiftTab="emit('shiftTab')"
    allowChoose
    ref="chooseRef"
    :fillAllPlace="isFillAllPlace"
    :hasMultipleValues="props.hasMultipleValues"
  >
    <template #item="{item, isSelected, itemHeight}: {item: IEntityItem, isSelected: boolean, itemHeight: number}">
      <ChooseItemComponent :isSelected="isSelected" :itemHeight="itemHeight">
        <template #default>
          <EntityItemContainer
            :entityType="item.entityType"
            :entityId="item.entityId"
            :thumbnailPayload="item.thumbnailPayload"
          >
            {{item.title}}
          </EntityItemContainer>
        </template>
      </ChooseItemComponent>
    </template>
    <template #skeletonItem="{index, itemHeight}">
      <ChooseItemSkeletonComponent :index="index" :itemHeight="itemHeight" :shape="ChooseItemSkeletonShapeType.Avatar"/>
    </template>
  </ChooseComponent>
</template>

<style scoped lang="scss">
  @use '../../../styles/variables' as variables;

.input, .selectable-item:deep(.checkbox:not(.is-checked)) {
  background: map-get(variables.$colors, container-low);

  &:focus {
    background: map-get(variables.$colors, container-lowest);
  }
}


.choose-users {
  width: 100%;
  max-width: 300px;
  max-height: 100%;
}

.dynamic-values-container {
  width: 100%;
  display: grid;
  grid-auto-columns: 1fr;
  padding: 0 map-get(variables.$spacing, space-2);
  box-sizing: border-box;
}

.empty {
  color: map-get(variables.$colors, content-dark);
  text-align: center;
  padding: map-get(variables.$spacing, space-1) map-get(variables.$spacing, space-2);
}
.dynamic-value {
  color: map-get(variables.$colors, primary);
  height: map-get(variables.$sizes, size-lg);
  box-sizing: border-box;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    border-radius: variables.$borderRadius;
    background-color: map-get(variables.$colors, overlay-05);
    cursor: pointer;
  }
}

.loading-wrapper {
  padding: map-get(variables.$spacing, space-5);
  display: flex;
  align-items: center;
  box-sizing: border-box;
  justify-content: center;
}

.search-wrapper {
  padding: 0 map-get(variables.$spacing, space-2);
}

.wrapper {
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: map-get(variables.$spacing, space-2);
  height: 100%;
  min-height: 0;
  max-height: 90vh;
  padding: map-get(variables.$spacing, space-2) 0;

  &.show-dynamic-value {
    grid-template-rows: auto auto 1fr;
  }
}

.users {
  padding: 0 map-get(variables.$spacing, space-2);
}
</style>
